import { useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { useStarterPrompts, type StarterRow } from "../hooks/useStarterPrompts";
import { useChatRail } from "./ChatRailProvider";
import { useCreateConversation } from "../hooks/useConversationTabs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";

async function invokeAgent(args: { agent_id: string; conversation_id: string; message: string }) {
  const { data: sessionRes } = await supabase.auth.getSession();
  const accessToken = sessionRes.session?.access_token;
  if (!accessToken) throw new Error("Not signed in.");
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const res = await fetch(`${supabaseUrl}/functions/v1/manna-agent-invoke`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({
      agent_id: args.agent_id,
      conversation_id: args.conversation_id,
      message: { role: "user", content: args.message },
      client_request_id: crypto.randomUUID(),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = body?.error?.code ?? "internal_error";
    const msg = body?.error?.message ?? `HTTP ${res.status}`;
    throw new Error(`${code}: ${msg}`);
  }
  return body as { run_id: string; conversation_id: string };
}

export function StarterPromptCards({ agentId }: { agentId: string }) {
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const qc = useQueryClient();
  const { data = [], isLoading } = useStarterPrompts(agentId);
  const { setActiveConvoId } = useChatRail();
  const create = useCreateConversation(agentId);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const launch = useMutation({
    mutationFn: async (starter: StarterRow) => {
      const convoId = await create.mutateAsync();
      setActiveConvoId(convoId);
      return invokeAgent({
        agent_id: agentId,
        conversation_id: convoId,
        message: starter.prompt,
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["conversation-messages", res.conversation_id] });
    },
    onSettled: () => setPendingId(null),
  });

  if (isLoading) {
    return (
      <Box flex={1} display="flex" alignItems="center" justifyContent="center">
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        flex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{ p: 3, color: T.textMute, fontSize: T.fs_meta, fontFamily: T.sans }}
      >
        Send a message to start a new conversation.
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: T.bg }}>
      <Typography
        sx={{
          color: T.textDim,
          fontSize: T.fs_micro,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1,
        }}
      >
        Starters
      </Typography>
      <Stack spacing={1}>
        {data.map((s) => (
          <Box
            key={s.id}
            component="button"
            type="button"
            onClick={() => {
              setPendingId(s.id);
              launch.mutate(s);
            }}
            disabled={pendingId !== null}
            sx={{
              textAlign: "left",
              border: `1px solid ${T.hair}`,
              bgcolor: T.rail,
              borderRadius: `${T.r_md}px`,
              p: 1.25,
              cursor: pendingId === null ? "pointer" : "wait",
              fontFamily: T.sans,
              "&:hover": { borderColor: T.accentBorder, bgcolor: T.panel },
            }}
          >
            {s.kicker && (
              <Typography sx={{ color: T.accent, fontSize: T.fs_micro, fontWeight: 600, mb: 0.25 }}>
                {s.kicker}
              </Typography>
            )}
            <Typography sx={{ color: T.text, fontSize: T.fs_label, fontWeight: 600 }}>
              {s.title}
            </Typography>
            {s.description && (
              <Typography sx={{ color: T.textDim, fontSize: T.fs_meta, mt: 0.25 }}>
                {s.description}
              </Typography>
            )}
            {pendingId === s.id && (
              <Typography sx={{ color: T.textMute, fontSize: T.fs_micro, mt: 0.5 }}>
                Starting…
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
